import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, ArrowUp, ArrowDown, HandCoins } from "lucide-react";
import { useApp } from "../context/AppContext";
import { gamesApi } from "../lib/api";
import { inr } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import { WinReveal } from "../components/WinReveal";
import { playCoinSplash, unlockAudio } from "../lib/rouletteSound";

const RANK = { 1: "A", 11: "J", 12: "Q", 13: "K" };
const label = (c) => RANK[c] || String(c);
// Deterministic suit purely for visual flair (red/black), not used by logic.
const suitFor = (c) => ["\u2660", "\u2665", "\u2666", "\u2663"][c % 4];
const isRed = (c) => c % 4 === 1 || c % 4 === 2;

function playFlip() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext; if (!Ctx) return;
    const ctx = new Ctx(); const o = ctx.createOscillator(), g = ctx.createGain();
    o.type = "square"; o.frequency.setValueAtTime(320, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(140, ctx.currentTime + 0.08);
    g.gain.setValueAtTime(0.12, ctx.currentTime); g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.14);
    o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.16);
    setTimeout(() => ctx.close().catch(() => {}), 400);
  } catch { /* audio blocked */ }
}

function Card({ c, flipping }) {
  const red = isRed(c);
  return (
    <div className={`relative mx-auto w-28 h-40 rounded-2xl grid place-items-center select-none ${flipping ? "animate-flip" : ""}`}
      style={{ background: "linear-gradient(160deg,#fdfdfd,#e9edf2)", boxShadow: "0 10px 30px rgba(0,0,0,0.5), inset 0 0 0 2px rgba(217,181,69,0.5)" }}
      data-testid="hilo-card">
      <span className={`absolute top-2 left-3 font-display font-black text-lg ${red ? "text-rose-600" : "text-slate-900"}`}>{label(c)}</span>
      <span className={`text-5xl ${red ? "text-rose-600" : "text-slate-900"}`}>{suitFor(c)}</span>
      <span className={`absolute bottom-2 right-3 font-display font-black text-lg rotate-180 ${red ? "text-rose-600" : "text-slate-900"}`}>{label(c)}</span>
    </div>
  );
}

export default function HiLoRush() {
  const { isAuthed, balance, refresh } = useApp();
  const { toast } = useToast();
  const [stake, setStake] = useState("100");
  const [round, setRound] = useState(null); // {roundId, card, multiplier, hiNext, loNext}
  const [busy, setBusy] = useState(false);
  const [flipping, setFlipping] = useState(false);
  const [status, setStatus] = useState(null); // null | "lost" | "won"
  const [reveal, setReveal] = useState(null);
  const [recent, setRecent] = useState([]);
  const streak = useRef(0);
  const stakeRef = useRef(0);

  if (!isAuthed) return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="hilo-signin-gate">
      <h2 className="font-display font-bold text-2xl">Sign in to play</h2>
      <Link to="/"><Button className="mt-6 font-semibold" data-testid="hilo-go-home">Go Home</Button></Link>
    </div>
  );

  const active = round && status === null;
  const potential = Math.round(stakeRef.current * (round?.multiplier || 1));

  const start = async () => {
    const s = Number(stake);
    if (!s || s < 20) { toast({ title: "Minimum bet is ₹20" }); return; }
    if (s > 100000) { toast({ title: "Higher bet limit crossed", description: "Please bet ₹100000 or below." }); return; }
    if (s > balance) { toast({ title: "Insufficient balance" }); return; }
    unlockAudio(); setBusy(true); setStatus(null);
    try {
      const res = await gamesApi.hiloStart({ stake: s });
      stakeRef.current = s;
      setRound(res);
      await refresh();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Try again" });
    } finally { setBusy(false); }
  };

  const guess = async (choice) => {
    if (!active || busy) return;
    setBusy(true); setFlipping(true); playFlip();
    try {
      const res = await gamesApi.hiloGuess({ roundId: round.roundId, choice });
      await new Promise((ok) => setTimeout(ok, 350));
      if (res.win) {
        setRound((r) => ({ ...r, card: res.card, multiplier: res.multiplier, hiNext: res.hiNext, loNext: res.loNext }));
      } else {
        setRound((r) => ({ ...r, card: res.card, multiplier: 0 }));
        setStatus("lost");
        streak.current = 0;
        setRecent((p) => [{ m: 0, won: false }, ...p].slice(0, 12));
      }
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Try again" });
    } finally { setFlipping(false); setBusy(false); }
  };

  const cashout = async () => {
    if (!active || busy || (round?.multiplier || 1) <= 1) return;
    setBusy(true);
    try {
      const res = await gamesApi.hiloCashout({ roundId: round.roundId });
      setStatus("won");
      streak.current += 1;
      setRecent((p) => [{ m: res.multiplier, won: true }, ...p].slice(0, 12));
      playCoinSplash();
      setReveal({ label: `${res.multiplier}x`, won: true, payout: res.payout, streak: streak.current });
      await refresh();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Try again" });
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-8 pb-28" data-testid="hilo-page">
      <WinReveal open={!!reveal} {...(reveal || {})} onClose={() => setReveal(null)} />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl flex items-center gap-2"><ArrowUp className="h-5 w-5 text-emerald-400" /><ArrowDown className="h-5 w-5 text-rose-400 -ml-2" /> Hi-Lo Rush</h1>
          <p className="text-sm text-muted-foreground">Higher or lower? Build your streak</p>
        </div>
        <span className="rounded-full bg-secondary px-3 py-1.5 text-sm font-semibold" data-testid="hilo-balance">
          <Coins className="h-4 w-4 inline mr-1 text-primary" />{inr(balance)}
        </span>
      </div>

      {/* Table */}
      <div className="relative rounded-2xl p-6 overflow-hidden"
        style={{ background: "radial-gradient(ellipse 120% 90% at 50% 0%, #10402c, #062017 70%)", border: "1px solid rgba(217,181,69,0.45)", boxShadow: "inset 0 0 60px rgba(0,0,0,0.5)" }}>
        {/* Multiplier header */}
        <div className="flex items-center justify-between mb-4">
          <div className="rounded-xl bg-black/30 px-3 py-1.5 ring-1 ring-white/10">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground block leading-none mb-0.5">Streak</span>
            <span className="font-display font-black text-lg text-yellow-300" data-testid="hilo-multiplier">{Number(round?.multiplier || 1).toFixed(2)}x</span>
          </div>
          <div className="rounded-xl bg-black/30 px-3 py-1.5 ring-1 ring-white/10 text-right">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground block leading-none mb-0.5">Cash value</span>
            <span className="font-display font-black text-lg text-primary" data-testid="hilo-potential">{round ? inr(potential) : "—"}</span>
          </div>
        </div>

        {round ? <Card c={round.card} flipping={flipping} /> : (
          <div className="mx-auto w-28 h-40 rounded-2xl grid place-items-center bg-black/30 ring-1 ring-white/10 text-muted-foreground text-sm">Deal a card</div>
        )}

        {/* Status line */}
        <div className="mt-4 h-6 text-center text-sm">
          {status === "lost" && <span className="font-semibold text-rose-400" data-testid="hilo-status-lost">Wrong card! Streak busted.</span>}
          {status === "won" && <span className="font-semibold text-yellow-300" data-testid="hilo-status-won">Cashed out {Number(round?.multiplier || 0).toFixed(2)}x!</span>}
          {active && <span className="text-emerald-100/70">Will the next card be higher or lower?</span>}
          {!round && <span className="text-muted-foreground">Set your stake and deal</span>}
        </div>

        {/* Guess buttons */}
        {active && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <button onClick={() => guess("hi")} disabled={busy} data-testid="hilo-higher-btn"
              className="rounded-xl bg-emerald-600/85 hover:bg-emerald-500 disabled:opacity-50 py-3 font-extrabold text-white transition-colors flex flex-col items-center">
              <span className="flex items-center gap-1"><ArrowUp className="h-4 w-4" /> Higher</span>
              <span className="text-[11px] font-semibold opacity-90">{Number(round.hiNext || 1).toFixed(2)}x</span>
            </button>
            <button onClick={() => guess("lo")} disabled={busy} data-testid="hilo-lower-btn"
              className="rounded-xl bg-rose-600/85 hover:bg-rose-500 disabled:opacity-50 py-3 font-extrabold text-white transition-colors flex flex-col items-center">
              <span className="flex items-center gap-1"><ArrowDown className="h-4 w-4" /> Lower</span>
              <span className="text-[11px] font-semibold opacity-90">{Number(round.loNext || 1).toFixed(2)}x</span>
            </button>
          </div>
        )}

        {active && (
          <Button className="mt-3 w-full h-11 font-extrabold" onClick={cashout} disabled={busy || (round?.multiplier || 1) <= 1} data-testid="hilo-cashout-btn">
            <HandCoins className="h-5 w-5 mr-1.5" />
            {(round?.multiplier || 1) <= 1 ? "Guess once to enable cash out" : `Cash Out ${inr(potential)}`}
          </Button>
        )}
      </div>

      {/* Controls */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-6" data-testid="hilo-controls">
        <label className="text-sm font-medium">Stake <span className="text-xs text-muted-foreground font-normal">(min ₹20 · max ₹100,000)</span></label>
        <Input type="number" min={20} max={100000} value={stake} disabled={active}
          onChange={(e) => setStake(e.target.value)} className="mt-1.5 disabled:opacity-60" data-testid="hilo-stake-input" />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {[20, 50, 100, 500, 1000, 2000, 5000, 10000].map((q) => {
            const special = q === 10000;
            return (
              <button key={q} onClick={() => !active && setStake(String(q))} disabled={active} data-testid={`hilo-chip-${q}`}
                className={special
                  ? "rounded-full px-3.5 py-1.5 text-sm font-extrabold text-black shadow-md ring-1 ring-yellow-200/70 hover:brightness-110 disabled:opacity-40 transition-[filter,opacity]"
                  : "rounded-full bg-secondary px-3 py-1 text-xs font-semibold hover:bg-secondary/70 disabled:opacity-40"}
                style={special ? { background: "linear-gradient(135deg,#fde68a,#f59e0b 55%,#b45309)" } : undefined}>
                +{q}
              </button>
            );
          })}
        </div>
        <Button className="mt-5 w-full h-12 font-extrabold text-base" onClick={start} disabled={busy || active} data-testid="hilo-deal-btn">
          {active ? "Round in progress…" : busy ? "Dealing…" : "Deal Card"}
        </Button>
        {recent.length > 0 && (
          <div className="mt-4 flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="hilo-recent">
            {recent.map((r, i) => (
              <span key={i} className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${r.won ? "bg-primary/15 text-yellow-300" : "bg-secondary text-muted-foreground"}`}>{Number(r.m).toFixed(2)}x</span>
            ))}
          </div>
        )}
        <p className="mt-3 text-xs text-muted-foreground text-center">A = 1, J/Q/K = 11/12/13. Equal counts as a win either way.</p>
      </div>
    </div>
  );
}
